import { useEffect, useState, useCallback, useMemo } from 'react'
import axios from 'axios'
import { accessSessionTokenKeyName, loggedInAdminAccessToken } from 'src/utils/constants'
import { CatalogColumn, SelectedColumn, ColumnCatalogResponse } from './types'
import { DefaultColumnOptions, getDefaultColumnsFromLegacyRules } from './defaultsFromLegacy'
import { normalizeFilterInputType, augmentSyncStatusFilterOptions } from '../listing-product-table-lite/filterUtils'

interface UseTableColumnsOptions {
  channelId: string | number
  initialSelectedColumns: SelectedColumn[]
  isPrimaryStore?: boolean
  defaultColumnOptions?: DefaultColumnOptions
  useApiColumnOrder?: boolean
  initialColumnOrderCustomized?: boolean
  onColumnOrderCustomizedChange?: (customized: boolean) => void
  mandatoryFields?: string[]
  bootstrapColumns?: CatalogColumn[]
  autoFetchCatalog?: boolean
}

export const useTableColumns = ({
  channelId,
  initialSelectedColumns,
  isPrimaryStore = false,
  defaultColumnOptions,
  useApiColumnOrder = false,
  initialColumnOrderCustomized,
  onColumnOrderCustomizedChange,
  mandatoryFields = ['product_name'],
  bootstrapColumns = [],
  autoFetchCatalog = false,
}: UseTableColumnsOptions) => {
  const isTruthyFlag = useCallback((value: any) => {
    if (value === true) return true
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      return normalized === 'true' || normalized === 'yes' || normalized === '1'
    }
    return false
  }, [])

  const isDefaultVisibleColumn = useCallback((col: any) => {
    if (!col) return false
    const advance = col?.advance_option || {}
    return (
      col?.allowVisibility === 'ALWAYS_VISIBLE' ||
      col?.allow_visiblity === 'ALWAYS_VISIBLE' ||
      col?.allow_visibility === 'ALWAYS_VISIBLE' ||
      Boolean(col?.fixed) ||
      isTruthyFlag(col?.default_visible) ||
      isTruthyFlag(col?.is_default) ||
      isTruthyFlag(col?.default) ||
      isTruthyFlag(advance?.default_visible) ||
      isTruthyFlag(advance?.is_default) ||
      isTruthyFlag(advance?.default)
    )
  }, [isTruthyFlag])

  const normalizeApiField = useCallback((field?: string) => {
    if (!field) return ''
    if (field === 'link_action') return 'link_actions'
    return field
  }, [])

  const [catalogColumns, setCatalogColumns] = useState<CatalogColumn[]>([])
  const [selectedColumns, setSelectedColumns] = useState<SelectedColumn[]>(initialSelectedColumns)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const isLinkingField = useCallback((field?: string) => {
    return field === 'linking_status' || field === 'link_actions'
  }, [])

  useEffect(() => {
    setIsInitialized(false)
    setError(null)
    setCatalogColumns([])
    setSelectedColumns([])
  }, [channelId])
  const mandatoryFieldsKey = (mandatoryFields || []).filter(Boolean).join('|')
  const normalizedMandatoryFields = useMemo(
    () => Array.from(new Set((mandatoryFields || []).filter(Boolean))),
    [mandatoryFieldsKey]
  )
  const hasConnectedChannels = useMemo(
    () => Array.isArray(defaultColumnOptions?.tabsData) && (defaultColumnOptions?.tabsData?.length || 0) > 0,
    [defaultColumnOptions?.tabsData?.length]
  )
  const shouldShowLinkingColumns = useMemo(
    () => defaultColumnOptions?.hasLinkingColumns !== false,
    [defaultColumnOptions?.hasLinkingColumns]
  )
  const bootstrapColumnsKey = (bootstrapColumns || [])
    .map((col) => normalizeApiField(col?.field || col?.id || ''))
    .filter(Boolean)
    .sort()
    .join('|')

  const areSelectedColumnsEqual = useCallback(
    (a: SelectedColumn[], b: SelectedColumn[]) => {
      if (a === b) return true
      if (a.length !== b.length) return false
      for (let i = 0; i < a.length; i += 1) {
        const left = a[i]
        const right = b[i]
        if (
          left.field !== right.field ||
          left.order !== right.order ||
          left.width !== right.width ||
          (left.pinned || null) !== (right.pinned || null)
        ) {
          return false
        }
      }
      return true
    },
    []
  )

  const ensureMandatoryColumns = useCallback(
    (
      columns: SelectedColumn[],
      catalog: CatalogColumn[] = catalogColumns,
      preserveOrder = initialColumnOrderCustomized !== false || !useApiColumnOrder
    ): SelectedColumn[] => {
      const catalogFieldSet = new Set((catalog || []).map((col) => col.field))
      const dedupedByField = new Map<string, SelectedColumn>()
      ;[...(columns || [])]
        .sort((a, b) => a.order - b.order)
        .forEach((col) => {
          if (!col?.field) return
          if (!shouldShowLinkingColumns && isLinkingField(col.field)) return
          if (catalogFieldSet.size > 0 && !catalogFieldSet.has(col.field)) return
          if (!dedupedByField.has(col.field)) {
            dedupedByField.set(col.field, col)
          }
        })
      const next = Array.from(dedupedByField.values())
      const existing = new Set(next.map((col) => col.field))
      const alwaysVisibleFields = catalog
        .filter((col) => col?.allowVisibility === 'ALWAYS_VISIBLE' || col?.fixed)
        .map((col) => col.field)
      const normalizedMandatory = Array.from(
        new Set([...(normalizedMandatoryFields || []), ...alwaysVisibleFields])
      )

      normalizedMandatory.forEach((field) => {
        if (!shouldShowLinkingColumns && isLinkingField(field)) return
        if (existing.has(field)) {
          return
        }
        if (catalogFieldSet.size > 0 && !catalogFieldSet.has(field)) {
          return
        }
        const catalogCol = catalog.find((col) => col.field === field)
        next.push({
          field,
          order: 0,
          width: catalogCol?.width,
          pinned: null,
        })
      })

      const catalogOrder = new Map((catalog || []).map((col, idx) => [col.field, idx]))
      const ordered = preserveOrder
        ? next
        : next.sort(
            (left, right) =>
              (catalogOrder.get(left.field) ?? Number.MAX_SAFE_INTEGER) -
              (catalogOrder.get(right.field) ?? Number.MAX_SAFE_INTEGER)
          )

      return ordered.map((col, idx) => ({ ...col, order: idx }))
    },
    [catalogColumns, initialColumnOrderCustomized, useApiColumnOrder, normalizedMandatoryFields, shouldShowLinkingColumns, isLinkingField]
  )

  const normalizeCatalogColumns = useCallback(
    (rawColumns: CatalogColumn[]): CatalogColumn[] => {
      const normalized: CatalogColumn[] = []
      const existingFields = new Set<string>()
      const isFairePlatform = Boolean(defaultColumnOptions?.isFairePlatform)
      const labelByField: Record<string, string> = {
        name: 'Product Name',
        product_name: 'Product Name',
        linking_status: 'Linking Status',
        link_actions: 'Link Actions',
        api_status: 'Status',
        sku: 'Channel SKU',
        api_id: 'SKU Id',
        quantity: 'Inventory Level',
        sync_status: 'Source Sync Status',
        accounting_sync_status: 'Accounting Sync Status',
        shipping_sync_status: 'Shipping Sync Status',
        erp_sync_status: 'ERP Sync Status',
        erp_secondary_sync_status: 'ERP Destination Sync Status',
        price: 'Price',
        compare_price: isFairePlatform ? 'Wholesale Price' : 'Compare Price',
        barcode: 'Barcode',
        api_code: 'Feed Id',
        created_at: 'Created At',
        updated_at: 'Updated At',
      }
      const addColumn = (field: string, width?: number) => {
        if (!field || existingFields.has(field)) return
        existingFields.add(field)
        normalized.push({
          id: `legacy-${field}`,
          name: labelByField[field] || field,
          field,
          label: labelByField[field] || field,
          type: 'text',
          sortable: true,
          filterable: true,
          pinnable: true,
          width,
        })
      }

      ;(rawColumns || []).forEach((col) => {
        const field = normalizeApiField(col?.field)
        if (!field || existingFields.has(field)) return
        if (isLinkingField(field) && !shouldShowLinkingColumns) {
          return
        }
        const rawVisibility =
          (col as any)?.allow_visiblity ||
          (col as any)?.allow_visibility ||
          col?.allowVisibility ||
          'YES'
        const allowVisibility: 'YES' | 'NO' | 'ALWAYS_VISIBLE' =
          rawVisibility === 'ALWAYS_VISIBLE' ? 'ALWAYS_VISIBLE' : rawVisibility === 'NO' ? 'NO' : 'YES'
        if (allowVisibility === 'NO') return

        const advance = (col as any)?.advance_option || col?.advance_option || {}
        const filterInputType = normalizeFilterInputType(advance, field)
        const headerName = (labelByField[field] || (col as any)?.headerName || col?.label || col?.name || field).trim()

        existingFields.add(field)
        normalized.push({
          ...col,
          field,
          id: col?.id || `api-${field}`,
          name: headerName,
          label: headerName,
          headerName,
          sortable:
            typeof advance?.allow_sorting === 'boolean'
              ? advance.allow_sorting
              : col?.sortable ?? true,
          filterable:
            filterInputType === 'DATE_RANGE' || field === 'created_at' || field === 'updated_at'
              ? true
              : typeof advance?.allow_filter === 'boolean'
              ? advance.allow_filter
              : col?.filterable ?? true,
          resizable:
            typeof advance?.allow_resize === 'boolean'
              ? advance.allow_resize
              : col?.resizable ?? true,
          filterInputType,
          filterOptions: augmentSyncStatusFilterOptions(
            field,
            Array.isArray(advance?.filter_options) ? advance.filter_options : []
          ),
          filterOperators: Array.isArray(advance?.filter_operators) ? advance.filter_operators : [],
          allowVisibility,
          fixed: allowVisibility === 'ALWAYS_VISIBLE' || Boolean(col?.fixed),
          advance_option: advance,
          type: filterInputType === 'DATE_RANGE' ? 'date' : col?.type || 'text',
        })
      })

      // Keep explicit mandatory fields available even if omitted by API.
      normalizedMandatoryFields.forEach((field) => addColumn(field))

      return normalized
    },
    [normalizedMandatoryFields, normalizeApiField, shouldShowLinkingColumns, isLinkingField]
  )

  const initializeSelectionFromCatalog = useCallback(
    (catalog: CatalogColumn[]) => {
      if (isInitialized) return
      if (!catalog.length) return

      const catalogFieldSet = new Set(catalog.map((c) => c.field))
      const validInitialSelection = (initialSelectedColumns || []).filter((col) =>
        catalogFieldSet.has(col.field)
      )
      const mandatorySet = new Set(normalizedMandatoryFields)
      const nonMandatoryInitialCount = validInitialSelection.filter(
        (col) => !mandatorySet.has(col.field)
      ).length
      const shouldUsePersistedSelection =
        validInitialSelection.length > 0 && nonMandatoryInitialCount > 0

      if (shouldUsePersistedSelection) {
        const preservePersistedOrder = initialColumnOrderCustomized !== false
        onColumnOrderCustomizedChange?.(preservePersistedOrder)

        const next = ensureMandatoryColumns(
          validInitialSelection,
          catalog,
          preservePersistedOrder || !useApiColumnOrder
        )
        setSelectedColumns((prev) => (areSelectedColumnsEqual(prev, next) ? prev : next))
      } else {
        onColumnOrderCustomizedChange?.(false)
        const legacyDefaultFields = getDefaultColumnsFromLegacyRules({
          ...(defaultColumnOptions || {}),
          mainStore: isPrimaryStore,
          hasLinkingColumns: shouldShowLinkingColumns,
        })
          .map((c) => c.field)
          .filter(Boolean)
        const legacyDefaultCatalog = legacyDefaultFields
          .map((field) => catalog.find((col) => col.field === field))
          .filter((col): col is CatalogColumn => Boolean(col))
        const defaultCatalog = catalog.filter((col: any) => isDefaultVisibleColumn(col))
        const defaultFields = new Set([
          ...legacyDefaultCatalog.map((col) => col.field),
          ...defaultCatalog.map((col) => col.field),
          ...normalizedMandatoryFields,
        ])
        const hasConfiguredDefaults = legacyDefaultCatalog.length > 0 || defaultCatalog.length > 0
        const apiOrderedCatalog = hasConfiguredDefaults
          ? catalog.filter((col) => defaultFields.has(col.field))
          : catalog
        const defaultCatalogRemainder = defaultCatalog.filter(
          (col) => !legacyDefaultCatalog.some((legacyCol) => legacyCol.field === col.field)
        )
        const legacyPreferredCatalog =
          legacyDefaultCatalog.length > 0
            ? [...legacyDefaultCatalog, ...defaultCatalogRemainder]
            : []
        const legacyOrderedCatalog =
          legacyPreferredCatalog.length > 0
            ? legacyPreferredCatalog
            : defaultCatalog.length > 0
              ? defaultCatalog
              : catalog
        const sourceCatalog = useApiColumnOrder ? apiOrderedCatalog : legacyOrderedCatalog
        const next = ensureMandatoryColumns(
          sourceCatalog.map((col, idx) => ({
            field: col.field,
            order: idx,
            width: col.width,
          })),
          catalog
        )
        setSelectedColumns((prev) => (areSelectedColumnsEqual(prev, next) ? prev : next))
      }
      setIsInitialized(true)
    },
    [
      isInitialized,
      initialSelectedColumns.length,
      initialSelectedColumns,
      ensureMandatoryColumns,
      normalizedMandatoryFields,
      areSelectedColumnsEqual,
      isPrimaryStore,
      isDefaultVisibleColumn,
      defaultColumnOptions,
      useApiColumnOrder,
      initialColumnOrderCustomized,
      onColumnOrderCustomizedChange,
      shouldShowLinkingColumns,
      isLinkingField,
    ]
  )

  // Fetch column catalog from API
  const fetchColumnCatalog = useCallback(async (options?: { syncSelection?: boolean }) => {
    const syncSelection = options?.syncSelection ?? true
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem(accessSessionTokenKeyName)
      const adminToken = localStorage.getItem(loggedInAdminAccessToken)
      const response = await axios.get<ColumnCatalogResponse>(
        `${process.env.REACT_APP_API_URL}/api/platform-product-variant-columns/${channelId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'xadmin': adminToken || null,
          },
        }
      )

      const catalog = normalizeCatalogColumns(response.data.columns || [])
      setCatalogColumns(catalog)
      if (syncSelection) {
        initializeSelectionFromCatalog(catalog)
        setSelectedColumns((prev) => {
          const next = ensureMandatoryColumns(prev, catalog)
          return areSelectedColumnsEqual(prev, next) ? prev : next
        })
      }
    } catch (err: any) {
      console.error('Error fetching column catalog:', err)
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch columns'
      if (err.response?.status === 403) {
        setError(`Access denied: ${errorMessage}. Please ensure you have admin access to this platform.`)
      } else {
        setError(errorMessage)
      }
      setCatalogColumns([])
    } finally {
      setLoading(false)
    }
  }, [channelId, normalizeCatalogColumns, initializeSelectionFromCatalog, ensureMandatoryColumns, areSelectedColumnsEqual])

  useEffect(() => {
    if (!bootstrapColumns.length) return
    const normalized = normalizeCatalogColumns(bootstrapColumns)
    setCatalogColumns((prev) => (prev.length ? prev : normalized))
    setSelectedColumns((prev) => {
      // Do not reconcile non-empty selection against bootstrap response columns,
      // because row API may return only currently requested subset and can drop
      // newly toggled fields on the first click.
      const next =
        prev.length === 0
          ? ensureMandatoryColumns(
              normalized.map((col, idx) => ({
                field: col.field,
                order: idx,
                width: col.width,
              })),
              normalized
            )
          : prev
      return areSelectedColumnsEqual(prev, next) ? prev : next
    })
  }, [bootstrapColumnsKey, bootstrapColumns, normalizeCatalogColumns, ensureMandatoryColumns, areSelectedColumnsEqual])

  useEffect(() => {
    if (!autoFetchCatalog) return
    if (bootstrapColumns.length) return
    fetchColumnCatalog()
  }, [autoFetchCatalog, bootstrapColumnsKey, bootstrapColumns.length, fetchColumnCatalog])

  // Update selected columns
  const updateSelectedColumns = useCallback((columns: SelectedColumn[]) => {
    const next = ensureMandatoryColumns(columns)
    setSelectedColumns((prev) => (areSelectedColumnsEqual(prev, next) ? prev : next))
  }, [ensureMandatoryColumns, areSelectedColumnsEqual])

  // Toggle column visibility
  const toggleColumnVisibility = useCallback((field: string) => {
    setSelectedColumns(prev => {
      const exists = prev.find(col => col.field === field)
      
      if (exists) {
        if (normalizedMandatoryFields.includes(field)) {
          return prev
        }
        // Remove column
        return ensureMandatoryColumns(prev
          .filter(col => col.field !== field)
          .map((col, idx) => ({ ...col, order: idx }))
        )
      } else {
        // Add column
        const maxOrder = prev.length > 0 ? Math.max(...prev.map(c => c.order)) : -1
        return ensureMandatoryColumns([...prev, { field, order: maxOrder + 1 }])
      }
    })
  }, [ensureMandatoryColumns, normalizedMandatoryFields])

  // Reorder columns
  const reorderColumns = useCallback((dragIndex: number, hoverIndex: number) => {
    setSelectedColumns(prev => {
      const sorted = [...prev].sort((a, b) => a.order - b.order)
      const [movedItem] = sorted.splice(dragIndex, 1)
      sorted.splice(hoverIndex, 0, movedItem)
      
      // Update order property
      return ensureMandatoryColumns(
        sorted.map((col, idx) => ({ ...col, order: idx })),
        catalogColumns,
        true
      )
    })
  }, [ensureMandatoryColumns, catalogColumns])

  // Pin column to left or right
  const pinColumn = useCallback((field: string, position: 'left' | 'right' | null) => {
    setSelectedColumns(prev => 
      prev.map(col => 
        col.field === field ? { ...col, pinned: position } : col
      )
    )
  }, [])

  // Get column by field
  const getColumnByField = useCallback((field: string): CatalogColumn | undefined => {
    return catalogColumns.find(col => col.field === field)
  }, [catalogColumns])

  // Check if column is visible
  const isColumnVisible = useCallback((field: string): boolean => {
    return selectedColumns.some(col => col.field === field)
  }, [selectedColumns])

  return {
    catalogColumns,
    selectedColumns,
    loading,
    error,
    updateSelectedColumns,
    toggleColumnVisibility,
    reorderColumns,
    pinColumn,
    getColumnByField,
    isColumnVisible,
    refetch: fetchColumnCatalog,
  }
}
